/**
 * El caso abierto: evidencia, contexto, notas e historial.
 *
 * El orden de la pantalla es el orden en que se mira un caso: primero **por
 * qué saltó** (la evidencia), después con qué está relacionado, y recién al
 * final quién lo tocó. Poner la gestión arriba haría que la evidencia quede
 * abajo del pliegue, que es la forma más efectiva de que nadie la lea.
 *
 * Acá no se renderiza el `unit_id`, aunque venga en la respuesta: el caso se
 * identifica por su número y la unidad por el lote al que pertenece.
 */
import type { ReactNode } from 'react';

import { useApiQuery, useKeysetQuery } from '@/api/hooks';
import { alertKeys, authKeys } from '@/api/keys';
import type { components } from '@/api/schema';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { CardBody, CardHeader } from '@/ui/Card';
import { ErrorState, LoadingState } from '@/ui/states';
import { EvidencePanel } from './Evidence';
import {
  allowedTransitions,
  alertTypeLabelKey,
  needsResolution,
  resolutionLabelKey,
  severityLabelKey,
  severityTone,
  statusLabelKey,
  statusTone,
  type AlertStatus,
} from './severity';
import type { CaseActions } from './useCaseActions';

type AlertDetailOut = components['schemas']['AlertDetailOut'];
type Note = components['schemas']['NoteOut'];
type AuditEntry = components['schemas']['AuditOut'];
type User = components['schemas']['UserOut'];

const NOTES_PAGE = 20;
const HISTORY_PAGE = 20;
const USERS_PAGE = 50;

export function AlertDetail({
  alertId,
  actions,
  canOperate,
  onRequestStatus,
  onRequestNote,
}: {
  alertId: number;
  actions: CaseActions;
  /** `analyst` para arriba. Un `viewer` lee la bandeja pero no la opera. */
  canOperate: boolean;
  /** Abre el diálogo de cambio de estado (necesita motivo al cerrar). */
  onRequestStatus: (status: AlertStatus) => void;
  onRequestNote: () => void;
}): ReactNode {
  const { t, fmt } = useI18n();
  const { claims, can } = useAuth();

  const detail = useApiQuery<AlertDetailOut>(
    alertKeys.detail(alertId),
    `/api/v1/alerts/${alertId}`,
  );
  const notes = useKeysetQuery<Note>(
    alertKeys.notes(alertId, NOTES_PAGE),
    `/api/v1/alerts/${alertId}/notes`,
    undefined,
    { limit: NOTES_PAGE },
  );
  const history = useKeysetQuery<AuditEntry>(
    alertKeys.history(alertId, HISTORY_PAGE),
    `/api/v1/alerts/${alertId}/history`,
    undefined,
    { limit: HISTORY_PAGE },
  );
  // Elegir a otra persona exige listar usuarios, y ese endpoint es de admin.
  // Un `analyst` se asigna a sí mismo o libera el caso; pedir la lista igual
  // sería un 403 garantizado en cada apertura de caso.
  const users = useKeysetQuery<User>(
    authKeys.users(USERS_PAGE),
    '/api/v1/auth/users',
    undefined,
    { limit: USERS_PAGE, enabled: canOperate && can('admin') },
  );

  if (detail.isPending) return <LoadingState rows={8} />;
  if (detail.isError) {
    return (
      <CardBody>
        <ErrorState error={detail.error} onRetry={() => { void detail.refetch(); }} />
      </CardBody>
    );
  }
  // `isPending`/`isError` ya dejan `data` definido: TanStack lo estrecha solo.
  const alert = detail.data;
  const typeKey = alertTypeLabelKey(alert.alert_type);
  const severityKey = severityLabelKey(alert.severity);
  const statusKey = statusLabelKey(alert.status);
  const transitions = allowedTransitions(alert.status);
  const assignedToMe = claims !== null && alert.assigned_to === claims.userId;

  const resolutionKey =
    alert.resolution === null || alert.resolution === undefined
      ? null
      : resolutionLabelKey(alert.resolution);
  const resolutionText =
    resolutionKey !== null ? t(resolutionKey) : (alert.resolution ?? '—');

  return (
    <>
      <CardHeader
        title={
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-ink-muted">{t('alerts.case', { id: alert.id })}</span>
            <span>{typeKey === null ? alert.alert_type : t(typeKey)}</span>
          </span>
        }
        description={t('alerts.detail.opened', { when: fmt.dateTime(alert.created_at) })}
        actions={
          <>
            <Badge tone={severityTone(alert.severity)}>
              {severityKey === null ? alert.severity : t(severityKey)}
            </Badge>
            <Badge tone={statusTone(alert.status)}>
              {statusKey === null ? alert.status : t(statusKey)}
            </Badge>
          </>
        }
      />

      <CardBody className="space-y-5">
        {/* ── la evidencia va primero: es la razón de ser de la pantalla ── */}
        <EvidencePanel alertType={alert.alert_type} evidence={alert.evidence} />

        <section aria-label={t('alerts.detail.context')}>
          <h3 className="mb-2 text-sm font-semibold text-ink">{t('alerts.detail.context')}</h3>
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            <Field label={t('alerts.column.batch')} value={alert.lot_code ?? '—'} />
            <Field label={t('alerts.detail.territory')} value={alert.batch_territory ?? '—'} />
            <Field
              label={t('alerts.detail.relatedUnit')}
              value={fmt.number(alert.related_unit_alerts)}
            />
            <Field
              label={t('alerts.detail.relatedBatch')}
              value={fmt.number(alert.related_batch_alerts)}
            />
            <Field
              label={t('alerts.detail.assignee')}
              value={alert.assignee_name ?? alert.assignee_email ?? t('alerts.unassigned')}
            />
            <Field label={t('alerts.detail.resolution')} value={resolutionText} />
          </dl>
        </section>

        {/* ─────────────────────────────────────────── gestión del caso ── */}
        {canOperate ? (
          <section aria-label={t('alerts.detail.actions')} className="space-y-3">
            <h3 className="text-sm font-semibold text-ink">{t('alerts.detail.actions')}</h3>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                variant={assignedToMe ? 'ghost' : 'secondary'}
                disabled={actions.isPending || assignedToMe || claims === null}
                onClick={() => {
                  if (claims !== null) actions.assign.mutate(claims.userId);
                }}
              >
                {t('alerts.action.assignSelf')}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={actions.isPending || alert.assigned_to === null}
                onClick={() => { actions.assign.mutate(null); }}
              >
                {t('alerts.action.unassign')}
              </Button>

              {can('admin') && users.items.length > 0 && (
                <label className="flex items-center gap-2 text-xs text-ink-subtle">
                  {t('alerts.action.assignTo')}
                  <select
                    className="h-8 rounded border border-border-strong bg-surface px-2 text-sm text-ink"
                    value={alert.assigned_to === null ? '' : String(alert.assigned_to)}
                    disabled={actions.isPending}
                    onChange={(event) => {
                      const value = event.target.value;
                      actions.assign.mutate(value === '' ? null : Number.parseInt(value, 10));
                    }}
                  >
                    <option value="">{t('alerts.unassigned')}</option>
                    {users.items.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.full_name ?? user.email}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {transitions.map((target) => {
                const label = statusLabelKey(target);
                return (
                  <Button
                    key={target}
                    size="sm"
                    variant={target === 'confirmed' ? 'critical' : 'secondary'}
                    disabled={actions.isPending}
                    onClick={() => {
                      if (needsResolution(target)) {
                        onRequestStatus(target);
                      } else {
                        actions.changeStatus.mutate({
                          status: target,
                          resolution: null,
                          note: null,
                        });
                      }
                    }}
                  >
                    {t('alerts.action.moveTo', { status: label === null ? target : t(label) })}
                  </Button>
                );
              })}
              <Button size="sm" variant="ghost" onClick={onRequestNote}>
                {t('alerts.action.note')}
              </Button>
            </div>

            {actions.error !== null && <ErrorState error={actions.error} />}
          </section>
        ) : (
          <p className="rounded border border-border bg-surface-sunken p-3 text-sm text-ink-muted">
            {t('alerts.detail.readOnly')}
          </p>
        )}

        {/* ──────────────────────────────────────────────────── notas ── */}
        <section aria-label={t('alerts.notes.title')}>
          <h3 className="mb-2 text-sm font-semibold text-ink">
            {t('alerts.notes.title')} ({fmt.number(alert.note_count)})
          </h3>
          {notes.query.isPending && <LoadingState rows={2} />}
          {notes.query.isError && <ErrorState error={notes.query.error} />}
          {!notes.query.isPending && notes.items.length === 0 && (
            <p className="text-sm text-ink-subtle">{t('alerts.notes.empty')}</p>
          )}
          <ol className="space-y-2">
            {notes.items.map((note) => (
              <li key={note.id} className="rounded border border-border bg-surface-sunken p-2">
                <p className="text-sm whitespace-pre-wrap text-ink">{note.body}</p>
                <p className="mt-1 text-xs text-ink-subtle">
                  {note.author_email ?? '—'} · {fmt.dateTime(note.created_at)}
                </p>
              </li>
            ))}
          </ol>
          {notes.hasMore && (
            <Button
              size="sm"
              variant="ghost"
              className="mt-2"
              onClick={notes.loadMore}
              disabled={notes.isLoadingMore}
            >
              {notes.isLoadingMore ? t('state.loadingMore') : t('state.loadMore')}
            </Button>
          )}
        </section>

        {/* ───────────────────────────────────────────────── historial ── */}
        <section aria-label={t('alerts.history.title')}>
          <h3 className="mb-2 text-sm font-semibold text-ink">{t('alerts.history.title')}</h3>
          <p className="mb-2 text-xs text-ink-subtle">{t('alerts.history.hint')}</p>
          {history.query.isPending && <LoadingState rows={2} />}
          {history.query.isError && <ErrorState error={history.query.error} />}
          {!history.query.isPending && history.items.length === 0 && (
            <p className="text-sm text-ink-subtle">{t('alerts.history.empty')}</p>
          )}
          <ol className="space-y-1">
            {history.items.map((entry) => (
              <li key={entry.id} className="flex flex-wrap gap-x-2 text-xs text-ink-muted">
                <span className="font-mono text-ink">{entry.action}</span>
                <span>{entry.actor_email ?? '—'}</span>
                <span>{fmt.dateTime(entry.created_at)}</span>
              </li>
            ))}
          </ol>
        </section>
      </CardBody>
    </>
  );
}

function Field({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}
